// A claim met by a measurement.
//
// The feed reports what channels SAID. Damage read off satellite imagery, and a
// thermal anomaly a satellite recorded, are things nobody chose to publish — so
// they turn "someone said X was hit" into "something at X was burning, and
// imagery shows a hole". That is the strongest thing this app can put in a feed.
//
// The damage half needs NO server work: damage markers and live events are both
// already in memory from the single `/api/entities?limit=10000` the panels do.
// The heat half needs `/api/live/heat` because FIRMS detections are not entities.
//
// TWO HONEST LIMITS, and both belong in the copy as much as in the code:
//   * A marker dated by imagery cannot say WHICH report made the hole, only that
//     something did. So the chip says "damage", never "confirmed strike".
//   * The entity list is capped at 10000 with no total, so ABSENCE of a chip is
//     not absence of damage. Never render "no corroboration" — render nothing.

import type { EntityDto } from './api';
import { get } from './api';
import { channelLabel } from './livekinds';
import { whenOf } from './when';

/** How close a damage marker must be to a situation's point. Wider than the
 *  marker's own precision because the EVENT's point is a geocode, not a survey —
 *  the reported place is often the town, not the facility. */
export const DAMAGE_NEAR_M = 3_000;

/** Imagery lags. A marker may be dated a little BEFORE the report (the analyst
 *  read an earlier "after" scene) and long after it, because the next usable
 *  cloud-free pass can be weeks out. Asymmetric for that reason. */
export const DAMAGE_BEFORE_MS = 2 * 24 * 3_600_000;
export const DAMAGE_AFTER_MS = 21 * 24 * 3_600_000;

export interface Corroboration {
  kind: 'damage' | 'heat' | 'echo';
  /** Short chip text, e.g. "damage 18 Jun" or "heat 1.2 km". */
  label: string;
  /** Full explanation — every chip must be able to say why it is there. */
  title: string;
  /** Entity to open (damage), if any. */
  entityId?: string;
}

function pointOf(e: EntityDto): [number, number] | null {
  return e.geometry?.type === 'Point'
    ? (e.geometry.coordinates as [number, number]) : null;
}

/** Centre of any geometry, cheaply — damage markers may be polygons (025). */
function centreOf(e: EntityDto): [number, number] | null {
  const g = e.geometry;
  if (!g) return null;
  if (g.type === 'Point') return g.coordinates as [number, number];
  if (g.type === 'Polygon') {
    const ring = (g.coordinates as number[][][])[0];
    if (!ring?.length) return null;
    let x = 0;
    let y = 0;
    for (const [lon, lat] of ring) { x += lon!; y += lat!; }
    return [x / ring.length, y / ring.length];
  }
  return null;
}

function distanceM(a: [number, number], b: [number, number]): number {
  const rad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * rad;
  const dLon = (b[0] - a[0]) * rad;
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(dLon / 2) ** 2;
  return 6371_000 * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

const DAY_FMT = new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short' });

/** The damage marker that best corroborates this event, or null.
 *
 *  "Best" is the NEAREST in space, not the closest in time: a hole 400 m from the
 *  reported point is better evidence than one 2.8 km away that happens to share a
 *  date, because the date came from an imagery pass and the distance came from
 *  someone looking at the pixels. */
export function damageFor(event: EntityDto, markers: EntityDto[]): Corroboration | null {
  const at = pointOf(event);
  if (!at) return null;
  const when = whenOf(event).at;
  if (!Number.isFinite(when)) return null;

  let best: { m: EntityDto; km: number; dated: number } | null = null;
  for (const marker of markers) {
    const centre = centreOf(marker);
    if (!centre) continue;
    // A marker's own occurrence clock is its imagery date (whenOf reads
    // attrs.date for exactly this), so there is one definition of "when".
    const dated = whenOf(marker).at;
    if (!Number.isFinite(dated)) continue;
    if (dated < when - DAMAGE_BEFORE_MS || dated > when + DAMAGE_AFTER_MS) continue;
    const d = distanceM(at, centre);
    if (d > DAMAGE_NEAR_M) continue;
    if (!best || d < best.km) best = { m: marker, km: d, dated };
  }
  if (!best) return null;

  const km = best.km / 1000;
  return {
    kind: 'damage',
    label: `damage ${DAY_FMT.format(new Date(best.dated))}`,
    // "shows damage", never "confirms the strike": imagery cannot attribute a
    // hole to one of several reports.
    title: `A damage marker ${km < 1 ? `${Math.round(best.km)} m` : `${km.toFixed(1)} km`}`
      + ` away shows damage as of ${DAY_FMT.format(new Date(best.dated))}.`
      + ' Imagery cannot say which report caused it, only that something did.'
      + ' Click to open the marker and its before/after scenes.',
    entityId: best.m.id,
  };
}

export interface NearbyReport {
  event: EntityDto;
  /** metres from the marker to the event's reported point */
  m: number;
  /** the event's occurrence, epoch ms */
  at: number;
}

/** The mirror of damageFor: the reported events a MARKER may document —
 *  same distance, same asymmetric window, read from the other end. This is
 *  what the damage-marker card lists, because the feed's "✔ damage" chip is
 *  a computed match, not a stored link, and a marker that shows nothing while
 *  the event beside it claims corroboration reads as a broken link.
 *
 *  Nearest first, capped: a card is a summary, not a query result. Same
 *  honest limit as the chip — the entity list is capped with no total, so an
 *  empty answer must render as NOTHING, never as "no nearby reports". */
export function eventsNear(marker: EntityDto, events: EntityDto[],
                           cap = 5): NearbyReport[] {
  const centre = centreOf(marker);
  if (!centre) return [];
  const dated = whenOf(marker).at;
  if (!Number.isFinite(dated)) return [];
  const out: NearbyReport[] = [];
  for (const event of events) {
    const at = pointOf(event);
    if (!at) continue;
    const when = whenOf(event).at;
    if (!Number.isFinite(when)) continue;
    // The SAME inequality damageFor applies, so the two directions can never
    // disagree about whether a pair matches.
    if (dated < when - DAMAGE_BEFORE_MS || dated > when + DAMAGE_AFTER_MS) continue;
    const d = distanceM(centre, at);
    if (d > DAMAGE_NEAR_M) continue;
    out.push({ event, m: d, at: when });
  }
  out.sort((a, b) => a.m - b.m);
  return out.slice(0, cap);
}

export interface HeatHit {
  km: number; at: string; satellite: string | null; unusual: boolean;
}

/** Thermal anomalies beside these events, keyed by event id.
 *
 *  The server only ever returns UNUSUAL detections — a persistent industrial
 *  flare beside a reported strike is not corroboration, and the baseline exists
 *  to know the difference. Never throws: a badge must not break the feed. */
export async function fetchHeat(ids: string[]): Promise<Record<string, HeatHit>> {
  const wanted = ids.slice(0, 200);
  if (!wanted.length) return {};
  try {
    const dto = await get<{ heat: Record<string, HeatHit> }>(
      `/api/live/heat?ids=${wanted.join(',')}`);
    return dto.heat ?? {};
  } catch {
    return {};
  }
}

const TIME_FMT = new Intl.DateTimeFormat(undefined,
  { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });

export interface MediaEcho { url: string; channel: string | null; at: string }

/** Events whose image was already posted EARLIER under a different incident —
 *  the recycled-footage caution (heat's mirror: heat says a sensor agrees,
 *  this says the pixels are old). Never throws; absence is "no echo found",
 *  never "verified original" — images are only hashed while the check has
 *  been live, so nothing may ever render a positive "original" claim. */
export async function fetchMediaEcho(ids: string[]): Promise<Record<string, MediaEcho>> {
  const wanted = ids.slice(0, 200);
  if (!wanted.length) return {};
  try {
    const dto = await get<{ echo: Record<string, MediaEcho> }>(
      `/api/live/media-echo?ids=${wanted.join(',')}`);
    return dto.echo ?? {};
  } catch {
    return {};
  }
}

export function echoChip(hit: MediaEcho): Corroboration | null {
  if (!hit?.url) return null;
  const at = Date.parse(hit.at);
  const day = Number.isFinite(at) ? DAY_FMT.format(new Date(at)) : 'earlier';
  return {
    kind: 'echo',
    label: `image seen ${day}`,
    // A caution, not a debunk — and the copy must say both halves: the words
    // are new, the pixels are not, and old pixels are not evidence either way.
    title: `This report's image matches one posted ${day}`
      + `${hit.channel ? ` by ${channelLabel(hit.channel)}` : ''} under a different report — `
      + 'possibly recycled footage. That does not make the event false; it '
      + 'means this picture is not evidence for it.',
  };
}

export function heatChip(event: EntityDto, hit: HeatHit): Corroboration | null {
  if (!hit?.unusual) return null;
  const detected = Date.parse(hit.at);
  const lag = Number.isFinite(detected)
    ? Math.round((detected - whenOf(event).at) / 60_000) : null;
  const when = lag === null ? ''
    : lag >= 0 ? ` ${lag} min after the report` : ` ${-lag} min before the report`;
  return {
    kind: 'heat',
    label: `heat ${hit.km} km`,
    title: `An unusual thermal anomaly ${hit.km} km away${when}`
      + `${hit.satellite ? ` (${hit.satellite})` : ''}, recorded `
      + `${Number.isFinite(detected) ? TIME_FMT.format(new Date(detected)) : 'nearby'}.`
      + ' Persistent industrial heat is excluded, so this is not a flare stack.',
  };
}
